import type Luminary from '@lumen5/luminary';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { BoundingBox, VideoPlanRequest } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';

export type ClipIDType = number;
export type ClipVersionIDType = number;

export const ORIGINAL_VERSION_ID = 0;

export enum ClipVersionStatus {
    PENDING = 'PENDING',
    LOADING = 'LOADING',
    LOADED = 'LOADED',
    FAILED = 'FAILED',
}

type ClipVersionCommonData = {
    clipId: ClipIDType;
    versionId: ClipVersionIDType;
    videoPlan?: VideoPlanRequest;
    luminary?: Luminary;
};
type PendingClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.PENDING;
};
type LoadingClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.LOADING;
};
type LoadedClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.LOADED;
    luminary: Luminary;
};
type FailedClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.FAILED;
};
export type ClipVersionData =
    | PendingClipVersionData
    | LoadingClipVersionData
    | LoadedClipVersionData
    | FailedClipVersionData;

type ClipVersionsInitialState = {
    allIds: ClipVersionIDType[];
    byId: Record<ClipVersionIDType, ClipVersionData>;
};

export type ClipData = {
    index: number;
    title: string;
    luminary: Luminary;
    hasCuts: boolean;
    versions: ClipVersionsInitialState;
    faceDetections: BoundingBox[] | null;
    videoId?: number;
    prompt?: string;
    downloaded?: boolean;
};

type ClipsInitialState = {
    allIds: ClipIDType[];
    byId: Record<ClipIDType, ClipData>;
    selected: ClipIDType | null;
};

const initialState: ClipsInitialState = {
    allIds: [],
    byId: {},
    selected: null,
};

export const clips = createSlice({
    name: 'clips',
    initialState: initialState,
    reducers: {
        addClipAndSelectAddedClip: (
            state,
            action: PayloadAction<{
                luminary: Luminary;
                title?: string;
                prompt?: string;
            }>,
        ) => {
            const nextIndex = calculateNextIndex(state);
            const title = action.payload.title || `Clip ${nextIndex}`;
            const luminary = action.payload.luminary;
            const prompt = action.payload.prompt;
            const clipId = nextIndex;
            const data: ClipData = {
                index: nextIndex,
                title,
                prompt,
                luminary,
                hasCuts: false,
                faceDetections: null,
                versions: {
                    allIds: [ORIGINAL_VERSION_ID],
                    byId: {
                        [ORIGINAL_VERSION_ID]: {
                            clipId,
                            versionId: ORIGINAL_VERSION_ID,
                            status: ClipVersionStatus.LOADED,
                            luminary,
                        },
                    },
                },
            };
            state.allIds.unshift(clipId); // add new clip to the beginning of the array
            state.byId[clipId] = data;
            state.selected = clipId;
        },
        deleteClipAndSelectNextClip: (state, action: PayloadAction<ClipIDType>) => {
            let idForSelection = state.selected;
            const idForDeletion = action.payload;
            const idForDeletionIndex = state.allIds.indexOf(idForDeletion);
            if (idForDeletion === state.selected) {
                const nextIdIndex =
                    idForDeletionIndex === state.allIds.length - 1
                        ? idForDeletionIndex - 1
                        : idForDeletionIndex + 1; // id+1 is necessary because allIds has not been spliced yet
                idForSelection = nextIdIndex === -1 ? null : state.allIds[nextIdIndex];
            }

            // store reference of old luminaries
            const luminariesToDestroy: Luminary[] = [];
            state.byId[idForDeletion].versions.allIds.forEach((versionId) => {
                const version = state.byId[idForDeletion].versions.byId[versionId];
                if (version.luminary) {
                    luminariesToDestroy.push(version.luminary);
                }
            });

            // update state
            state.allIds.splice(idForDeletionIndex, 1);
            delete state.byId[idForDeletion];
            state.selected = idForSelection;

            // destroy old luminary without waiting
            luminariesToDestroy.forEach((luminary) => luminary.destroy());
        },
        setSelectedClipId: (state, action: PayloadAction<ClipIDType>) => {
            state.selected = action.payload;
        },
        setClipHasCuts: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; hasCuts: boolean }>,
        ) => {
            const { clipId, hasCuts } = action.payload;
            state.byId[clipId].hasCuts = hasCuts;
        },
        setVideoIdForClipId: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; videoId: number }>,
        ) => {
            state.byId[action.payload.clipId].videoId = action.payload.videoId;
        },
        setClipDownloaded: (state, action: PayloadAction<{ clipId: ClipIDType }>) => {
            state.byId[action.payload.clipId].downloaded = true;
        },
        setClipTitle: (state, action: PayloadAction<{ clipId: ClipIDType; title: string }>) => {
            const { clipId, title } = action.payload;
            if (state.byId[clipId]) {
                state.byId[clipId].title = title;
            }
        },
        setClipPrompt: (state, action: PayloadAction<{ clipId: ClipIDType; prompt: string }>) => {
            const { clipId, prompt } = action.payload;
            if (state.byId[clipId]) {
                state.byId[clipId].prompt = prompt;
            }
        },
        updateClipVersionLuminary: (
            state,
            action: PayloadAction<{
                clipId: ClipIDType;
                versionId: ClipVersionIDType;
                luminary: Luminary;
            }>,
        ) => {
            const { clipId, versionId, luminary } = action.payload;
            const oldLuminary = state.byId[clipId].versions.byId[versionId].luminary;
            state.byId[clipId].versions.byId[versionId].luminary = luminary;
            if (oldLuminary) {
                oldLuminary.destroy();
            }
        },
        selectClipVersion: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; versionId: ClipVersionIDType }>,
        ) => {
            const { clipId, versionId } = action.payload;
            const clip = state.byId[clipId];
            if (!clip) {
                throw new Error(`Clip with ID ${clipId} not found`);
            }
            const version = clip.versions.byId[versionId];
            if (!version) {
                throw new Error(`Version with ID ${versionId} not found`);
            }
            if (version.status !== ClipVersionStatus.LOADED) {
                throw new Error(`Version with ID ${versionId} is not loaded`);
            }
            clip.luminary = version.luminary;
        },
        deleteAllClipVersions: (state, action: PayloadAction<{ clipId: ClipIDType }>) => {
            const { clipId } = action.payload;
            const clip = state.byId[clipId];
            if (!clip) {
                throw new Error(`Clip with ID ${clipId} not found`);
            }

            const versionIdsToDelete = clip.versions.allIds.filter(
                (versionId) => versionId !== ORIGINAL_VERSION_ID,
            );
            const luminariesToDestroy = versionIdsToDelete
                .map((versionId) => {
                    const version = clip.versions.byId[versionId];
                    return version.luminary;
                })
                .filter((luminary) => luminary !== undefined);

            clip.versions.allIds = [ORIGINAL_VERSION_ID];
            versionIdsToDelete.forEach((versionId) => {
                delete clip.versions.byId[versionId];
            });

            luminariesToDestroy.forEach((luminary) => luminary.destroy());
        },
        setFaceDetectionsForClipId: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; faceDetections: BoundingBox[] }>,
        ) => {
            const { clipId, faceDetections } = action.payload;
            state.byId[clipId].faceDetections = faceDetections;
        },
    },
    selectors: {
        getAllClipIds: (state) => state.allIds,
        getClipIdsWithCuts: (state) => state.allIds.filter((id) => state.byId[id].hasCuts),
        getClipById: (state, id: number) => state.byId[id],
        getCountAllClips: (state: ClipsInitialState) => {
            return state.allIds.length;
        },
        getCountClipsToSave: (state) => {
            return state.allIds.filter((id) => state.byId[id].hasCuts).length;
        },
        getSelectedClipId: (state: ClipsInitialState) => state.selected,
        getSelectedClip: (state: ClipsInitialState) => {
            if (state.selected) {
                return state.byId[state.selected];
            }
            return undefined;
        },
        getClipVersionIds: (state: ClipsInitialState, clipId: ClipIDType) => {
            const clip = state.byId[clipId];
            return clip.versions.allIds;
        },
        getClipVersionById: (
            state: ClipsInitialState,
            clipId: ClipIDType,
            versionId: ClipVersionIDType,
        ) => {
            const clip = state.byId[clipId];
            return clip.versions.byId[versionId];
        },
        getFaceDetectionsForClipId: (state: ClipsInitialState, clipId: ClipIDType) => {
            return state.byId[clipId].faceDetections;
        },
    },
});

const findMaxIndex = (state: ClipsInitialState) => {
    let maxIndex = 0;
    if (state.allIds.length > 0) {
        state.allIds.forEach((id) => {
            const clip = state.byId[id];
            maxIndex = Math.max(maxIndex, clip.index);
        });
    }
    return maxIndex;
};

const calculateNextIndex = (state: ClipsInitialState) => {
    const maxIndex = findMaxIndex(state);
    return maxIndex + 1;
};

export const {
    addClipAndSelectAddedClip,
    deleteClipAndSelectNextClip,
    setSelectedClipId,
    setClipHasCuts,
    setVideoIdForClipId,
    setClipDownloaded,
    setClipTitle,
    setClipPrompt,
    updateClipVersionLuminary,
    selectClipVersion,
    deleteAllClipVersions,
    setFaceDetectionsForClipId,
} = clips.actions;

export const {
    getAllClipIds,
    getClipIdsWithCuts,
    getClipById,
    getCountAllClips,
    getCountClipsToSave,
    getSelectedClipId,
    getSelectedClip,
    getClipVersionIds,
    getClipVersionById,
    getFaceDetectionsForClipId,
} = clips.getSelectors((state: RootState) => state.clip_creator.clips);
